import { FC, PropsWithChildren } from 'react';
import { LoginMethod } from '@/api/auth';
import BlankLayout from '@/layouts/BlankLayout';
import ClassroomsLayout from '@/layouts/ClassroomsLayout';
import LessonsLayout from '@/layouts/LessonsLayout';
import LoginLayout from '@/layouts/LoginLayout';
import PortfolioLayout from '@/layouts/PortfolioLayout';
import SessionLayout from '@/layouts/SessionLayout';
import UserProfileLayout from '@/layouts/UserProfileLayout';
import { UserRole } from '@/model/User.model';
import { lazyWithReload } from '@/router/utils';
import Error from '@/routes/Error';

const ActivitySessionV2SplashScreenController = lazyWithReload(
  () => import('@/routes/ActivitySessionV2/splashScreen/ActivitySessionV2SplashScreenController')
);
const ActivityIntroController = lazyWithReload(
  () => import('@/routes/ActivitySessionV2/ActivityIntro/ActivityIntroController')
);
const ActivitySessionV2 = lazyWithReload(() => import('@/routes/ActivitySessionV2/ActivitySessionV2'));
const ActivitySessionV2Complete = lazyWithReload(() => import('@/routes/ActivitySessionV2Complete'));
const TwoWayPronunciationFeedbackController = lazyWithReload(
  () =>
    import('@/routes/ActivitySessionV2/taskComponents/TwoWayPronunciationFeedback/TwoWayPronunciationFeedbackController')
);
const TwoWayListeningReplayController = lazyWithReload(
  () => import('@/routes/ActivitySessionV2/taskComponents/TwoWayListeningReplay/TwoWayListeningReplayController')
);
const Classrooms = lazyWithReload(() => import('@/routes/Classrooms/Classrooms'));
const Home = lazyWithReload(() => import('@/routes/Home/Home'));
const Leaderboard = lazyWithReload(() => import('@/routes/Leaderboard/Leaderboard'));
const LeaderboardPodium = lazyWithReload(() => import('@/routes/LeaderboardPodium/LeaderboardPodium'));
const LessonActivities = lazyWithReload(() => import('@/routes/LessonActivities/LessonActivities'));
const LessonActivityReportParamsResolver = lazyWithReload(
  () => import('@/routes/LessonActivityReport/LessonActivityReportParamsResolver')
);
const LessonComplete = lazyWithReload(() => import('@/routes/LessonComplete'));
const LessonDetail = lazyWithReload(() => import('@/routes/LessonDetail'));
const LessonReport = lazyWithReload(() => import('@/routes/LessonReport'));
const LessonReportDetail = lazyWithReload(() => import('@/routes/LessonReportDetail'));
const LessonReportDetailV2 = lazyWithReload(() => import('@/routes/LessonReportDetailV2'));
const LessonsPage = lazyWithReload(() => import('@/routes/Lessons/LessonsPage'));
const LiveSessionGamePage = lazyWithReload(() => import('@/routes/LiveSession/game/LiveSessionGamePage'));
const LiveSessionResultPage = lazyWithReload(() => import('@/routes/LiveSession/result/LiveSessionResultPage'));
const LoginPage = lazyWithReload(() => import('@/routes/Login/LoginPage'));
const SignupPageController = lazyWithReload(() => import('@/routes/Signup/SignupPageController'));
const EnrollActivationPageController = lazyWithReload(() => import('@/routes/Enroll/EnrollActivationPageController'));
const EnrollPageController = lazyWithReload(() => import('@/routes/Enroll/EnrollPageController'));
const EnrollPaymentPageController = lazyWithReload(() => import('@/routes/Enroll/EnrollPaymentPageController'));
const EnrollPaymentReturnPageController = lazyWithReload(
  () => import('@/routes/Enroll/EnrollPaymentReturnPageController')
);
const ForgotPassword = lazyWithReload(() => import('@/routes/ForgotPassword/ForgotPassword'));
const UserNotebook = lazyWithReload(() => import('@/routes/UserNotebook/UserNotebook'));
const UserProfile = lazyWithReload(() => import('@/routes/UserProfile/UserProfile'));
const SplashScreen = lazyWithReload(() => import('@/routes/SplashScreen/SplashScreenPage'));
const AvatarSelectionPage = lazyWithReload(() => import('@/routes/Avatar/AvatarSelectionPage'));
const ClaimXPPage = lazyWithReload(() => import('@/routes/Gamification/ClaimXPPage'));
const ClaimCoinPage = lazyWithReload(() => import('@/routes/Gamification/ClaimCoinPage'));
const PdfRenderer = lazyWithReload(() => import('@/routes/internalAdminApp/PdfRenderer'));
const AccessPortfolio = lazyWithReload(() => import('@/routes/Portfolio/AccessPortfolio'));
const VoicePortfolio = lazyWithReload(() => import('@/routes/Portfolio/VoicePortfolio'));

export type RouteData = {
  key?: string;
  path: string;
  description?: string;
  component: FC<Record<string, unknown>>;
  errorComponent?: FC;
  layoutComponent?: FC<PropsWithChildren>;
  layoutComponentProps?: Record<string, unknown>;
  // Keep this type flexible since we destructure keys from params
  // oxlint-disable-next-line no-explicit-any
  getRoute: (params?: any) => string;
  getFallbackRoute?: (params?: Record<string, unknown>) => string;
  children?: RouteData[];
  remark?: string;
  roles?: UserRole[];
};

enum RouteKeys {
  Avatar = 'Avatar',
  Classrooms = 'Classrooms',
  Lessons = 'Lessons',
  LiveSessionGame = 'LiveSessionGame',
  LiveSessionResult = 'LiveSessionResult',
  LessonActivities = 'LessonActivities',
  LessonComplete = 'LessonComplete',
  LessonDetail = 'LessonDetail',
  LessonReport = 'LessonReport',
  LessonReportDetail = 'LessonReportDetail',
  LessonReportDetailV2 = 'LessonReportDetailV2',
  LessonActivityReport = 'LessonActivityReport',
  Leaderboard = 'Leaderboard',
  LeaderboardPodium = 'LeaderboardPodium',
  ActivitySessionV2SplashScreen = 'ActivitySessionV2SplashScreen',
  ActivitySessionV2ActivityIntro = 'ActivitySessionV2ActivityIntro',
  ActivitySessionV2 = 'ActivitySessionV2',
  ActivitySessionV2Complete = 'ActivitySessionV2Complete',
  ActivitySessionV2PronunciationFeedback = 'ActivitySessionV2PronunciationFeedback',
  ActivitySessionV2ListeningReplay = 'ActivitySessionV2ListeningReplay',
  Profile = 'Profile',
  SplashScreen = 'SplashScreen',
  UserNotebook = 'UserNotebook',
  XP = 'XP',
  Coin = 'Coin',
}

enum PublicRouteKeys {
  Home = 'Home',
  Login = 'Login',
  Signup = 'Signup',
  ForgotPassword = 'ForgotPassword',
  PdfRenderer = 'PdfRenderer',
  Portfolio = 'Portfolio',
  PortfolioVoice = 'PortfolioVoice',
  EnrollmentActivation = 'EnrollmentActivation',
  Enrollment = 'Enrollment',
  EnrollmentPayment = 'EnrollmentPayment',
  EnrollmentPaymentComplete = 'EnrollmentPaymentComplete',
}

const publicRoutes = {
  [PublicRouteKeys.Home]: {
    path: '/',
    component: Home,
    errorComponent: Error,
    layoutComponent: LoginLayout,
    getRoute: () => {
      return '/login';
    },
  },
  [PublicRouteKeys.Login]: {
    path: '/login',
    component: LoginPage,
    errorComponent: Error,
    layoutComponent: LoginLayout,
    getRoute: (params?: { method?: LoginMethod }) => {
      const method = params?.method;
      return `/login${method ? `?method=${method}` : ''}`;
    },
  },
  [PublicRouteKeys.Signup]: {
    path: '/signup',
    component: SignupPageController,
    errorComponent: Error,
    layoutComponent: LoginLayout,
    getRoute: (params?: { code: string }) => {
      const code = params?.code;
      return `/signup${code ? `?code=${code}` : ''}`;
    },
  },
  [PublicRouteKeys.ForgotPassword]: {
    path: '/forgot-password',
    component: ForgotPassword,
    errorComponent: Error,
    layoutComponent: LoginLayout,
    getRoute: () => {
      return '/forgot-password' as const;
    },
  },
  [PublicRouteKeys.PdfRenderer]: {
    path: `pdf-renderer/:type/:templateName`,
    component: PdfRenderer,
    errorComponent: Error,
    getRoute: ({ type, templateName }: { type: string; templateName: string }) => {
      return `/pdf-renderer/${type}/${templateName}`;
    },
  },
  [PublicRouteKeys.Portfolio]: {
    path: '/portfolio',
    component: AccessPortfolio,
    errorComponent: Error,
    layoutComponent: PortfolioLayout,
    getRoute: () => {
      return '/portfolio';
    },
  },
  [PublicRouteKeys.PortfolioVoice]: {
    path: '/portfolio/voice',
    component: VoicePortfolio,
    errorComponent: Error,
    layoutComponent: PortfolioLayout,
    getRoute: () => {
      return '/portfolio/voice';
    },
  },
  [PublicRouteKeys.EnrollmentActivation]: {
    path: '/enrollment',
    component: EnrollActivationPageController,
    errorComponent: Error,
    getRoute: () => {
      return '/enrollment';
    },
  },
  [PublicRouteKeys.Enrollment]: {
    path: '/enrollment/:code',
    component: EnrollPageController,
    errorComponent: Error,
    getRoute: ({ code }: { code: string }) => {
      return `/enrollment/${code}`;
    },
  },
  [PublicRouteKeys.EnrollmentPayment]: {
    path: '/enrollment/:code/payment',
    component: EnrollPaymentPageController,
    errorComponent: Error,
    getRoute: ({ code }: { code: string }) => {
      return `/enrollment/${code}/payment`;
    },
  },
  [PublicRouteKeys.EnrollmentPaymentComplete]: {
    path: '/enrollment/:code/payment/complete',
    component: EnrollPaymentReturnPageController,
    errorComponent: Error,
    getRoute: ({ code, orderId }: { code: string; orderId: string }) => {
      return `/enrollment/${code}/payment/complete?orderId=${orderId}`;
    },
  },
} satisfies Record<PublicRouteKeys, RouteData>;

const authenticatedRoutes = {
  [RouteKeys.Avatar]: {
    path: '/avatar',
    component: AvatarSelectionPage,
    errorComponent: Error,
    layoutComponent: BlankLayout,
    getRoute: ({ redirectUrl, isFirstTime }: { redirectUrl: string; isFirstTime?: boolean }) => {
      const searchParams = new URLSearchParams({ redirectUrl });
      if (isFirstTime !== undefined) {
        searchParams.set('isFirstTime', String(isFirstTime));
      }
      return `/avatar?${searchParams.toString()}`;
    },
  },
  [RouteKeys.SplashScreen]: {
    path: '/splash-screen',
    component: SplashScreen,
    errorComponent: Error,
    layoutComponent: BlankLayout,
    getRoute: () => {
      return '/splash-screen';
    },
  },
  [RouteKeys.Lessons]: {
    path: '/classrooms/:classroomId/lessons',
    component: LessonsPage,
    errorComponent: Error,
    layoutComponent: LessonsLayout,
    getRoute: ({ classroomId, tab }: { classroomId: string; tab?: 'competition' }) => {
      return tab ? `/classrooms/${classroomId}/lessons?tab=${tab}` : `/classrooms/${classroomId}/lessons`;
    },
  },
  [RouteKeys.LiveSessionGame]: {
    path: '/classrooms/:classroomId/live-sessions/:sessionId',
    component: LiveSessionGamePage,
    errorComponent: Error,
    layoutComponent: BlankLayout,
    getRoute: ({ classroomId, sessionId }: { classroomId: string; sessionId: string }) => {
      return `/classrooms/${classroomId}/live-sessions/${sessionId}`;
    },
  },
  [RouteKeys.LiveSessionResult]: {
    path: '/classrooms/:classroomId/live-sessions/:sessionId/result',
    component: LiveSessionResultPage,
    errorComponent: Error,
    layoutComponent: BlankLayout,
    getRoute: ({ classroomId, sessionId }: { classroomId: string; sessionId: string }) => {
      return `/classrooms/${classroomId}/live-sessions/${sessionId}/result`;
    },
  },
  [RouteKeys.Classrooms]: {
    path: '/classrooms',
    component: Classrooms,
    errorComponent: Error,
    layoutComponent: ClassroomsLayout,
    getRoute: () => {
      return `/classrooms`;
    },
  },
  /**
   * @deprecated Old lesson detail for legacy and scaffolding lesson types.
   */
  [RouteKeys.LessonDetail]: {
    path: '/classrooms/:classroomId/lessons/:lessonId',
    component: LessonDetail,
    errorComponent: Error,
    layoutComponent: SessionLayout,
    getRoute: ({ classroomId, lessonId }: { classroomId: string; lessonId: string }) => {
      return `/classrooms/${classroomId}/lessons/${lessonId}`;
    },
  },
  [RouteKeys.LessonActivities]: {
    path: '/lesson-sessions/:lessonInstanceId/activities',
    component: LessonActivities,
    errorComponent: Error,
    layoutComponent: BlankLayout,
    getRoute: ({ lessonInstanceId }: { lessonInstanceId: string }) => {
      return `/lesson-sessions/${lessonInstanceId}/activities`;
    },
  },
  [RouteKeys.LessonComplete]: {
    path: '/lesson-sessions/:lessonInstanceId/complete',
    component: LessonComplete,
    errorComponent: Error,
    layoutComponent: SessionLayout,
    getRoute: ({ lessonInstanceId }: { lessonInstanceId: string }) => {
      return `/lesson-sessions/${lessonInstanceId}/complete`;
    },
  },
  [RouteKeys.ActivitySessionV2SplashScreen]: {
    path: '/lesson-sessions/:lessonInstanceId/activity-sessions-v2/:activityInstanceId/splash-screen',
    component: ActivitySessionV2SplashScreenController,
    errorComponent: Error,
    layoutComponent: SessionLayout,
    getRoute: ({ lessonInstanceId, activityInstanceId }: { lessonInstanceId: string; activityInstanceId: string }) => {
      return `/lesson-sessions/${lessonInstanceId}/activity-sessions-v2/${activityInstanceId}/splash-screen`;
    },
  },

  [RouteKeys.ActivitySessionV2ActivityIntro]: {
    path: '/lesson-sessions/:lessonInstanceId/activity-sessions-v2/:activityInstanceId/activity-intro',
    component: ActivityIntroController,
    errorComponent: Error,
    layoutComponent: SessionLayout,
    getRoute: ({ lessonInstanceId, activityInstanceId }: { lessonInstanceId: string; activityInstanceId: string }) => {
      return `/lesson-sessions/${lessonInstanceId}/activity-sessions-v2/${activityInstanceId}/activity-intro`;
    },
  },
  [RouteKeys.ActivitySessionV2]: {
    path: '/lesson-sessions/:lessonInstanceId/activity-sessions-v2/:activityInstanceId/',
    component: ActivitySessionV2,
    errorComponent: Error,
    layoutComponent: SessionLayout,
    getRoute: ({ lessonInstanceId, activityInstanceId }: { lessonInstanceId: string; activityInstanceId: string }) => {
      return `/lesson-sessions/${lessonInstanceId}/activity-sessions-v2/${activityInstanceId}`;
    },
  },
  [RouteKeys.ActivitySessionV2Complete]: {
    path: '/lesson-sessions/:lessonInstanceId/activity-sessions-v2/:activityInstanceId/complete',
    component: ActivitySessionV2Complete,
    errorComponent: Error,
    layoutComponent: SessionLayout,
    getRoute: ({ lessonInstanceId, activityInstanceId }: { lessonInstanceId: string; activityInstanceId: string }) => {
      return `/lesson-sessions/${lessonInstanceId}/activity-sessions-v2/${activityInstanceId}/complete`;
    },
  },
  [RouteKeys.ActivitySessionV2PronunciationFeedback]: {
    path: '/lesson-sessions/:lessonInstanceId/activity-sessions-v2/:activityInstanceId/pronunciation-feedback',
    component: TwoWayPronunciationFeedbackController,
    errorComponent: Error,
    layoutComponent: SessionLayout,
    getRoute: ({ lessonInstanceId, activityInstanceId }: { lessonInstanceId: string; activityInstanceId: string }) => {
      return `/lesson-sessions/${lessonInstanceId}/activity-sessions-v2/${activityInstanceId}/pronunciation-feedback`;
    },
  },
  [RouteKeys.ActivitySessionV2ListeningReplay]: {
    path: '/lesson-sessions/:lessonInstanceId/activity-sessions-v2/:activityInstanceId/listening-replay',
    component: TwoWayListeningReplayController,
    errorComponent: Error,
    layoutComponent: SessionLayout,
    getRoute: ({ lessonInstanceId, activityInstanceId }: { lessonInstanceId: string; activityInstanceId: string }) => {
      return `/lesson-sessions/${lessonInstanceId}/activity-sessions-v2/${activityInstanceId}/listening-replay`;
    },
  },
  [RouteKeys.LessonReport]: {
    path: '/reports/lesson-sessions/:lessonInstanceId',
    component: LessonReport,
    errorComponent: Error,
    layoutComponent: SessionLayout,
    getRoute: ({ lessonInstanceId }: { lessonInstanceId: string }) => {
      return `/reports/lesson-sessions/${lessonInstanceId}`;
    },
  },
  [RouteKeys.LessonReportDetail]: {
    path: '/reports/lesson-sessions/:lessonInstanceId/detail',
    component: LessonReportDetail,
    errorComponent: Error,
    layoutComponent: BlankLayout,
    getRoute: ({ lessonInstanceId }: { lessonInstanceId: string }) => {
      return `/reports/lesson-sessions/${lessonInstanceId}/detail`;
    },
  },
  [RouteKeys.LessonReportDetailV2]: {
    path: '/reports/lesson-sessions/:lessonInstanceId/detail-v2',
    component: LessonReportDetailV2,
    errorComponent: Error,
    layoutComponent: BlankLayout,
    getRoute: ({ lessonInstanceId }: { lessonInstanceId: string }) => {
      return `/reports/lesson-sessions/${lessonInstanceId}/detail-v2`;
    },
  },
  [RouteKeys.LessonActivityReport]: {
    path: '/reports/lesson-sessions/:lessonInstanceId/activity/:activityId',
    component: LessonActivityReportParamsResolver,
    errorComponent: Error,
    layoutComponent: BlankLayout,
    getRoute: ({ lessonInstanceId, activityId }: { lessonInstanceId: string; activityId: string }) => {
      return `/reports/lesson-sessions/${lessonInstanceId}/activity/${activityId}`;
    },
  },
  [RouteKeys.Leaderboard]: {
    path: '/leaderboard/:classroomId',
    component: Leaderboard,
    errorComponent: Error,
    layoutComponent: BlankLayout,
    getRoute: ({ classroomId }: { classroomId: string }) => {
      return `/leaderboard/${classroomId}`;
    },
  },
  [RouteKeys.UserNotebook]: {
    path: '/notebook',
    component: UserNotebook,
    errorComponent: Error,
    layoutComponent: ClassroomsLayout,
    getRoute: () => {
      return `/notebook`;
    },
  },
  [RouteKeys.Profile]: {
    path: '/profile',
    component: UserProfile,
    errorComponent: Error,
    layoutComponent: UserProfileLayout,
    getRoute: () => {
      return `/profile`;
    },
  },
  [RouteKeys.LeaderboardPodium]: {
    path: '/leaderboard',
    component: LeaderboardPodium,
    errorComponent: Error,
    layoutComponent: UserProfileLayout,
    getRoute: ({ query }: { query?: string }) => {
      return `/leaderboard?${query}`;
    },
  },
  [RouteKeys.XP]: {
    path: '/lesson-sessions/:lessonInstanceId/activity-sessions/:activityInstanceId/xp',
    component: ClaimXPPage,
    errorComponent: Error,
    layoutComponent: BlankLayout,
    getRoute: ({ lessonInstanceId, activityInstanceId }: { lessonInstanceId: string; activityInstanceId: string }) => {
      return `/lesson-sessions/${lessonInstanceId}/activity-sessions/${activityInstanceId}/xp`;
    },
  },
  [RouteKeys.Coin]: {
    path: '/lesson-sessions/:lessonInstanceId/activity-sessions/:activityInstanceId/coin',
    component: ClaimCoinPage,
    errorComponent: Error,
    layoutComponent: BlankLayout,
    getRoute: ({ lessonInstanceId, activityInstanceId }: { lessonInstanceId: string; activityInstanceId: string }) => {
      return `/lesson-sessions/${lessonInstanceId}/activity-sessions/${activityInstanceId}/coin`;
    },
  },
} satisfies Record<RouteKeys, RouteData>;

export { authenticatedRoutes, publicRoutes };
